/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import * as markers from "constants/perf-markers";
import { sendSentryPerf } from "../utils/SentryUtils";
import { logPageRenderTime, logPreviewInitTime } from "../logging";
import { MAX_LOG_FOR_PER_PAGE_RENDER_TIME } from "../constants/constants";

let recordedTimings = {};
let pageRenderTimes = {};
let totalPagesRendered = 0;
let liquidModeResourceRequestTimes = {};
let liquidModeResourceResponseTimes = {};
let dropinQuickToolLoadedTimes = {};
let dropinQuickToolEnabledTimes = {};
// TODO : queueWaitTimes, ajsTotalTimes, mainToWorkerTimes, workerToMainTimes are temporary.
// Should be removed after prod analysis is complete.
let queueWaitTimes = {};
let ajsTotalTimes = {};
let mainToWorkerTimes = {};
let workerToMainTimes = {};

export const resetPerfTimings = () => {
  recordedTimings = {};
  pageRenderTimes = {};
  queueWaitTimes = {};
  ajsTotalTimes = {};
  mainToWorkerTimes = {};
  workerToMainTimes = {};
  totalPagesRendered = 0;
  liquidModeResourceRequestTimes = {};
  liquidModeResourceResponseTimes = {};
  dropinQuickToolLoadedTimes = {};
  dropinQuickToolEnabledTimes = {};
};

export const recordPerformanceTimings = timings => {
  for (const key in timings) {
    recordedTimings[key] = timings[key];
  }
};

export const getRecordedTimings = () => ({
  ...recordedTimings
});

export const getTimingsToLogWithFirstPageLoad = index => {
  const recordedTimings = getRecordedTimings();
  const ajsFirstPageLoadStart = recordedTimings[markers.PAGE_LOAD][index][markers.PAGE_LOAD_START_TIME];
  const externalFirstPageLoadStart = recordedTimings[markers.PAGE_LOAD][index][markers.EXTERNAL_PAGE_LOAD_START_TIME];
  delete recordedTimings[markers.PAGE_LOAD];
  const firstPageRenderTime = pageRenderTimes[index][0];
  const firstPageQueueWaitTime = queueWaitTimes[index][0];
  const firstPageAjsTotalTime = ajsTotalTimes[index][0];
  const firstPageMainToWorker = mainToWorkerTimes[index][0];
  const firstPageWorkerToMain = workerToMainTimes[index][0];
  const isPageProgressivelyRendered = !!recordedTimings[markers.FIRST_PAGE_FIRST_BIT_LOAD_TIME];
  if (window.performance && window.performance.mark && ajsFirstPageLoadStart) {
    window.performance.mark(formScopedMarkerName(markers.AJS_FIRST_PAGE_LOAD_START_TIME), {
      startTime: ajsFirstPageLoadStart
    });
  }
  return {
    ...recordedTimings,
    ajsFirstPageLoadStart,
    externalFirstPageLoadStart,
    firstPageRenderTime,
    isPageProgressivelyRendered,
    firstPageQueueWaitTime,
    firstPageAjsTotalTime,
    firstPageMainToWorker,
    firstPageWorkerToMain
  };
};

// checks if given script name is our main bundle
const isMainScript = name => /^.*dcpreviewdropin\/.*\/bootstrap\.js$/.test(name);

export const recordMainScriptLoadTime = () => {
  if (window.performance && typeof window.performance.getEntriesByType === "function") {
    const entries = performance.getEntriesByType("resource").filter(i => isMainScript(i.name));
    if (Array.isArray(entries) && entries.length) {
      recordPerformanceTimings({
        [markers.PREVIEW_JS_LOAD_START]: Math.round(entries[entries.length - 1].startTime),
        [markers.PREVIEW_JS_LOAD_END]: Math.round(entries[entries.length - 1].responseEnd)
      });
    }
  }
};

const formScopedMarkerName = markerName => `dropin_preview_${markerName}`;

const recordNow = (markerName, time) => {
  if (!(markerName in recordedTimings)) {
    recordedTimings[markerName] = Math.round(time || performance.now());
    if (window.performance && window.performance.mark && !time) {
      return window.performance.mark(formScopedMarkerName(markerName));
    }
  }
};

// This function is used to extract the timings of markers from the window.performance
export const recordExternalTimings = (externalMarkers = markers.externalMarkers) => {
  externalMarkers.forEach(markerData => {
    recordPerfMarks(markerData);
  });
};

export const recordPreviewInit = () => {
  recordNow(markers.PREVIEW_INIT);
  logPreviewInitTime(getPreviewInitTime());
};

export const recordAJSResourceRequestTime = resourceID => {
  liquidModeResourceRequestTimes[resourceID] = performance.now();
};

export const recordAJSResourceResponseTime = (resourceID, resourceSize, resourceType) => {
  liquidModeResourceResponseTimes[resourceID] = [performance.now(), resourceSize, resourceType];
};

export const recordDropinQuickToolLoadedTime = dropinId => {
  dropinQuickToolLoadedTimes[dropinId] = performance.now();
};

export const recordDropinQuickToolEnabledTime = dropinId => {
  dropinQuickToolEnabledTimes[dropinId] = performance.now();
};

export const recordFirstPageLoaded = (conversionDelta = 0) => {
  const mark = recordNow(markers.FIRST_PAGE_LOADED);
  sendSentryPerf({ metricName: markers.FIRST_PAGE_LOADED, value: mark.startTime });
  recordedTimings[markers.FIRST_PAGE_LOADED] -= conversionDelta;
  recordExternalTimings();
};

export const recordTimeToViewFirstPage = () => {
  recordNow(markers.FIRST_PAGE_RENDERED);
};

export const recordMetadataStart = () => recordNow(markers.GET_METADATA_START);

export const recordMetadataEnd = ({
  openPDFWasmTime,
  openPDFQueueWaitTime,
  openPDFTotalTime,
  openPDFMainToWorker,
  openPDFWorkerToMain
}) => {
  recordNow(markers.GET_METADATA_END);
  recordPerformanceTimings({
    openPDFWasmTime,
    openPDFQueueWaitTime,
    openPDFTotalTime,
    openPDFMainToWorker,
    openPDFWorkerToMain
  });
};

const recordPageLoadTimingData = (index, key) => {
  if (!(markers.PAGE_LOAD in recordedTimings)) {
    recordedTimings[markers.PAGE_LOAD] = {};
  }
  const pageLoadTimes = recordedTimings[markers.PAGE_LOAD];
  if (!(index in pageLoadTimes)) {
    pageLoadTimes[index] = {
      [key]: Math.round(performance.now())
    };
  } else if (!(key in pageLoadTimes[index])) {
    pageLoadTimes[index][key] = Math.round(performance.now());
  }
};

export const recordPageLoadStartTime = (index, pageRenditionType = "ajs") => {
  const key = pageRenditionType === "external" ? markers.EXTERNAL_PAGE_LOAD_START_TIME : markers.PAGE_LOAD_START_TIME;
  recordPageLoadTimingData(index, key);
};

export const recordPagePartiallyLoaded = index => {
  recordPageLoadTimingData(index, markers.PAGE_PARTIALLY_LOADED);
};

export const recordFirstTileLoadedForPage = index => {
  recordPageLoadTimingData(index, markers.FIRST_TILE_LOADED_FOR_PAGE);
};

export const recordExternalPageLoadStartTime = index => {
  recordPageLoadStartTime(index, "external");
};
export const recordPageLoadEndTime = index => {
  recordPageLoadTimingData(index, markers.PAGE_LOAD_END_TIME);
};

export const recordPageRenderTime = (index, extraParams) => {
  if (!pageRenderTimes[index]) pageRenderTimes[index] = [];
  if (!queueWaitTimes[index]) queueWaitTimes[index] = [];
  if (!ajsTotalTimes[index]) ajsTotalTimes[index] = [];
  if (!mainToWorkerTimes[index]) mainToWorkerTimes[index] = [];
  if (!workerToMainTimes[index]) workerToMainTimes[index] = [];
  pageRenderTimes[index].push(Math.round(extraParams.renderTime));
  queueWaitTimes[index].push(Math.round(extraParams.queueWaitTime));
  ajsTotalTimes[index].push(Math.round(extraParams.ajsTotalTime));
  mainToWorkerTimes[index].push(Math.round(extraParams.mainToWorkerTime));
  workerToMainTimes[index].push(Math.round(extraParams.workerToMainTime));
  if (totalPagesRendered < MAX_LOG_FOR_PER_PAGE_RENDER_TIME) {
    const renderingStats = {
      PageRenderTime: extraParams.renderTime,
      QueueWaitTime: extraParams.queueWaitTime,
      AjsTotalTime: extraParams.ajsTotalTime,
      MainToWorkerTime: extraParams.mainToWorkerTime,
      WorkerToMainTime: extraParams.workerToMainTime,
      TotalPageRenderTime: Math.round(performance.now() - extraParams.startTime),
      RenderedViaTiling: extraParams.renderedViaTiling,
      PageType: extraParams.type === "cache" ? "cache" : "visible",
      PageNo: index,
      IsPageProgressivelyRendered: false
    };
    logPageRenderTime(renderingStats);
  }
  totalPagesRendered += 1;
};

export const recordProgressivePageRenderTime = (index, renderingStats) => {
  if (!pageRenderTimes[index]) pageRenderTimes[index] = [];
  if (!queueWaitTimes[index]) queueWaitTimes[index] = [];
  if (!ajsTotalTimes[index]) ajsTotalTimes[index] = [];
  if (!mainToWorkerTimes[index]) mainToWorkerTimes[index] = [];
  if (!workerToMainTimes[index]) workerToMainTimes[index] = [];
  pageRenderTimes[index].push(Math.round(renderingStats.PageRenderTime));
  queueWaitTimes[index].push(Math.round(renderingStats.QueueWaitTime));
  ajsTotalTimes[index].push(Math.round(renderingStats.AjsTotalTime));
  mainToWorkerTimes[index].push(Math.round(renderingStats.MainToWorkerTime));
  workerToMainTimes[index].push(Math.round(renderingStats.WorkerToMainTime));
  if (totalPagesRendered < MAX_LOG_FOR_PER_PAGE_RENDER_TIME) {
    logPageRenderTime(renderingStats);
  }
  totalPagesRendered += 1;
};

export const getPageRenderTimes = () => ({ pageRenderTimes });

export const getTotalPagesRendered = () => totalPagesRendered;

export const recordExternalPageLoadEndTime = index => {
  recordPageLoadTimingData(index, markers.EXTERNAL_PAGE_LOAD_END_TIME);
};

export const recordFirstPageReloadTime = () => recordNow(markers.FIRST_PAGE_RELOAD_TIME);

export const recordExternalMetadataEnd = () => recordNow(markers.EXTERNAL_META_DATA_END_TIME);

export const recordPerfMarks = markerData => {
  if (window.performance && typeof window.performance.getEntriesByName === "function") {
    if ("name" in markerData) {
      const entries = window.performance.getEntriesByName(markerData.name, "mark");
      if (Array.isArray(entries) && entries.length) {
        const mark = entries[entries.length - 1];
        recordNow(mark.name, mark.startTime);
      }
    } else if ("prefix" in markerData) {
      const entries = window.performance.getEntriesByType("mark").filter(i => i.name.startsWith(markerData.prefix));
      const markerSet = new Set();
      for (let i = entries.length - 1; i >= 0; --i) {
        const mark = entries[i];
        const markerName = mark.name.slice(markerData.prefix.length);
        if (!markerSet.has(markerName)) {
          recordNow(markerName, mark.startTime);
          markerSet.add(markerName);
        }
      }
    }
  }
};

export const recordFirstRasterLoaded = () => {
  recordNow(markers.FIRST_RASTER_LOADED);
};

export const recordProgressiveRenderingFirstResponseTimeForPage = index => {
  recordPageLoadTimingData(index, markers.FIRST_PROGRESSIVE_RENDERING_RESPONSE_FOR_PAGE);
};

export const getViewerInitTime = () => recordedTimings[markers.VIEWER_INIT] || recordedTimings[markers.PREVIEW_INIT];

export const getPreviewInitTime = () => recordedTimings[markers.PREVIEW_INIT];

export const getAJSLoadTime = () => getPreviewInitTime();

export const getFirstPageLoadTime = () => recordedTimings[markers.FIRST_PAGE_LOADED] - getViewerInitTime();

export const getFirstRasterLoadTime = () => recordedTimings[markers.FIRST_RASTER_LOADED] - getViewerInitTime();

export const getAJSResourceFetchTimings = () => {
  let timeTaken = [];
  for (const [key, value] of Object.entries(liquidModeResourceRequestTimes)) {
    if (liquidModeResourceResponseTimes[key]) {
      timeTaken.push([
        key,
        liquidModeResourceResponseTimes[key][0] - value,
        liquidModeResourceResponseTimes[key][1],
        liquidModeResourceResponseTimes[key][2]
      ]);
    }
  }
  return timeTaken;
};

export const getDropinQuickToolLoadedTime = dropinId => dropinQuickToolLoadedTimes[dropinId];

export const getDropinQuickToolEnabledTime = dropinId => dropinQuickToolEnabledTimes[dropinId];

export const recordFirstPageProgressiveAdditionalTimes = ({ firstAndLastBitTimeDelta = 0, blobConversionTime = 0 }) => {
  const lastBitConvertedTime = performance.now() - blobConversionTime;
  const firstBitConvertedTime = lastBitConvertedTime - firstAndLastBitTimeDelta;

  recordNow(markers.FIRST_PAGE_FIRST_BIT_LOAD_TIME, firstBitConvertedTime);
  recordNow(markers.FIRST_PAGE_LAST_BIT_LOAD_TIME, lastBitConvertedTime);
};

export const recordDocJsEngineInitStart = () => {
  recordNow(markers.DOC_JS_ENGINE_INIT_START);
};

export const recordDocJsEngineInitEnd = () => {
  recordNow(markers.DOC_JS_ENGINE_INIT_END);
};

export const recordPageJsEngineInitStart = () => {
  recordNow(markers.PAGE_JS_ENGINE_INIT_START);
};

export const recordPageJsEngineInitEnd = () => {
  recordNow(markers.PAGE_JS_ENGINE_INIT_END);
};

export const recordExecuteDocOpenActionsStart = () => {
  recordNow(markers.EXECUTE_DOC_OPEN_ACTIONS_START);
};

export const recordExecuteDocOpenActionsEnd = () => {
  recordNow(markers.EXECUTE_DOC_OPEN_ACTIONS_END);
};

// perfObj should be a performance marker object
export const calculateNetworkSpeed = perfObj => {
  if (
    perfObj &&
    perfObj.responseEnd !== undefined &&
    perfObj.responseStart !== undefined &&
    perfObj.encodedBodySize !== undefined
  ) {
    const durationInMs = perfObj.responseEnd - perfObj.responseStart;
    const durationInSec = durationInMs / 1000;
    const downloadSizeInByte = perfObj.encodedBodySize;
    const bitsLoaded = downloadSizeInByte * 8;
    const speedBps = (bitsLoaded / durationInSec).toFixed(2);
    const speedKbps = (speedBps / 1000).toFixed(2);
    const speedMbps = (speedKbps / 1000).toFixed(2);
    return speedMbps;
  }
  return -1;
};

export const recordCNPDFSwitchBegin = () => recordNow(markers.CNPDF_SWITCH_BEGIN);
export const recordCNPDFSwitchEnd = () => recordNow(markers.CNPDF_SWITCH_END);

export const getCNPDFSwitchBeginTime = () => recordedTimings[markers.CNPDF_SWITCH_BEGIN];

export const getPDFToCNPDFSwitchTime = () =>
  recordedTimings[markers.CNPDF_SWITCH_END] - recordedTimings[markers.CNPDF_SWITCH_BEGIN];
